import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProductDetails } from "../api/api";
import { CartContext } from "../context/CartContext";
import CartButton from "../components/CartButton";

function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [toastMessage, setToastMessage] = useState(null);
  const { addToCart } = useContext(CartContext);

  useEffect(() => {
    fetchProductDetails(id).then(setProduct);
  }, [id]);

  if (!product)
    return <p style={styles.loading}>⏳ جاري تحميل التفاصيل...</p>;

  const calculateTotal = () => {
    let total = Number(product.price);
    Object.values(selectedOptions).flat().forEach((option) => {
      const price = parseFloat(option.price_diff);
      if (!isNaN(price)) total += price;
    });
    return (total * quantity).toFixed(2);
  };

  const toggleOption = (groupId, option) => {
    setSelectedOptions((prev) => {
      const groupOptions = prev[groupId] || [];
      const exists = groupOptions.find((o) => o.id === option.id);

      return {
        ...prev,
        [groupId]: exists
          ? groupOptions.filter((o) => o.id !== option.id)
          : [...groupOptions, option],
      };
    });
  };

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      base_price: Number(product.price),
      selected_options: selectedOptions,
      total_price: Number(calculateTotal()),
      image: product.image,
      quantity: quantity,
    };

    addToCart(cartItem);
    showToast("✅ تمت إضافة المنتج إلى السلة!");
  };

  return (
    <div style={styles.container}>
      {toastMessage && <div style={styles.toast}>{toastMessage}</div>}

      {/* Header */}
      <header style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          ⟵
        </button>
        <CartButton />
      </header>
      {/* Product Image */}
      <div style={styles.imageContainer}>
        <img
          src={`https://al-asala-restaurant.com/api/storage/${product.image}`}
          alt={product.name}
          style={styles.productImage}
        />
      </div>

      {/* Product Info */}
      <div style={styles.content}>
        <h1 style={styles.productName}>{product.name}</h1>
        <p style={styles.price}>💰 <strong>{product.price} ₪</strong></p>

        {product.description && (
          <p style={styles.description}>{product.description}</p>
        )}

        {/* Option Groups */}
        {product.option_groups?.length > 0 && (
          <div style={styles.optionsSection}>
            <h3 style={styles.sectionTitle}>🧩 الإضافات</h3>
            {product.option_groups.map((group) => (
              <div key={group.id} style={styles.optionGroup}>
                <h4 style={styles.optionGroupTitle}>{group.name}</h4>
                <div style={styles.optionGrid}>
                  {group.options.map((option) => {
                    const price = parseFloat(option.price_diff);
                    const label = !isNaN(price)
                      ? `${option.name} (${price >= 0 ? "+" : "-"}${Math.abs(
                          price
                        ).toFixed(2)}₪)`
                      : `${option.name}`;

                    const isChecked = (selectedOptions[group.id] || []).some(
                      (o) => o.id === option.id
                    );

                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(group.id, option)}
                        style={{
                          ...styles.optionButton,
                          ...(isChecked ? styles.optionButtonActive : {}),
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quantity Selector */}
        <div style={styles.quantitySection}>
          <span>الكمية:</span>
          <div style={styles.quantityWrapper}>
            <button
              style={styles.quantityBtn}
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              −
            </button>
            <span style={styles.quantityText}>{quantity}</span>
            <button
              style={styles.quantityBtn}
              onClick={() => setQuantity(quantity + 1)}
            >
              +
            </button>
          </div>
        </div>

        {/* Total Price & Add to Cart */}
        <div style={styles.footer}>
          <h3 style={styles.totalPrice}>💵 الإجمالي: {calculateTotal()} ₪</h3>
          <button style={styles.addToCartButton} onClick={handleAddToCart}>
            🛒 أضف للسلة
          </button>
        </div>
      </div>
    </div>
  );
}

/* 🎨 Styles */
const styles = {
  container: {
    fontFamily: "'Cairo', sans-serif",
    backgroundColor: "#fffaf3",
    minHeight: "100vh",
    position: "relative",
  },
  toast: {
    position: "fixed",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#28a745",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    boxShadow: "0 5px 12px rgba(0,0,0,0.1)",
    zIndex: 999,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 18px",
    backgroundColor: "#fff4e2",
    borderRadius: "0 0 18px 18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  backButton: {
    padding: "8px 12px",
    borderRadius: "50%",
    backgroundColor: "#ffb788",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    color: "#fff",
  },
  imageContainer: {
    textAlign: "center",
    padding: "16px 0",
  },
  productImage: {
    width: "100%",
    maxWidth: "300px",
    height: "auto",
    borderRadius: "16px",
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  content: {
    padding: "18px",
  },
  productName: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#3e2e26",
    textAlign: "center",
  },
  price: {
    fontSize: "18px",
    textAlign: "center",
    color: "#ff7b3b",
    fontWeight: "700",
    marginTop: "8px",
    marginBottom: "16px",
  },
  description: {
    fontSize: "15px",
    lineHeight: "1.6",
    color: "#5a524e",
    textAlign: "center",
    marginBottom: "18px",
  },
  optionsSection: {
    marginTop: "10px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#a25432",
    marginBottom: "16px",
  },
  optionGroup: {
    marginBottom: "20px",
  },
  optionGroupTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#6e584d",
    marginBottom: "12px",
  },
  optionGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  optionButton: {
    padding: "8px 12px",
    borderRadius: "10px",
    border: "1px solid #dec6b3",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "13px",
  },
  optionButtonActive: {
    backgroundColor: "#ff995f",
    color: "white",
    borderColor: "#ff7f3f",
  },
  quantitySection: {
    marginTop: "20px",
    textAlign: "center",
  },
  quantityWrapper: {
    marginTop: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
  },
  quantityBtn: {
    width: "34px",
    height: "34px",
    fontSize: "18px",
    borderRadius: "50%",
    backgroundColor: "#ff9b62",
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },
  quantityText: {
    fontSize: "18px",
    minWidth: "32px",
    textAlign: "center",
    color: "#3d2f28",
  },
  footer: {
    textAlign: "center",
    marginTop: "28px",
    paddingBottom: "40px",
  },
  totalPrice: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#a04d2b",
    marginBottom: "16px",
  },
  addToCartButton: {
    backgroundColor: "#ff7b3b",
    color: "white",
    padding: "14px 30px",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "16px",
    boxShadow: "0 6px 18px rgba(255,110,60,0.4)",
  },
  loading: {
    textAlign: "center",
    padding: "60px",
    fontSize: "16px",
    color: "#777",
  },
};

export default ProductDetails;
